import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Calculator, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, IDR, DATE, formatErr, NUM } from "@/lib/api";
import DataTable, { EmptyState } from "@/components/app/DataTable";
import { canApprove, useAuth } from "@/lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function HppList() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [products, setProducts] = useState({});
  const [mixDesigns, setMixDesigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openStd, setOpenStd] = useState(false);
  const [selectedMD, setSelectedMD] = useState("");
  const [targetQty, setTargetQty] = useState(100);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: hs }, { data: ps }, { data: mds }] = await Promise.all([
        api.get("/hpp"), api.get("/products"), api.get("/mix-designs"),
      ]);
      setRows(hs); setProducts(Object.fromEntries(ps.map((p) => [p.id, p]))); setMixDesigns(mds);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const calcStd = async () => {
    try {
      const { data } = await api.post("/hpp/calc-standard", { mix_design_id: selectedMD, target_qty: Number(targetQty), save: true });
      toast.success("HPP Standar berhasil dihitung");
      setOpenStd(false); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const approve = async (r) => {
    try { await api.post(`/hpp/${r.id}/approve`); toast.success("HPP disetujui"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <span className="gc-label">Analisis</span>
          <h1 className="gc-h1 mt-1">Perhitungan HPP</h1>
          <p className="mt-2 text-slate-500 max-w-2xl">Daftar perhitungan HPP standar (dari mix design) dan aktual (dari batch produksi).</p>
        </div>
        <button onClick={() => setOpenStd(true)} className="gc-btn-primary" data-testid="btn-calc-std"><Calculator className="w-4 h-4" /> Hitung HPP Standar</button>
      </div>

      {loading ? <div className="gc-card"><Loader2 className="w-4 h-4 animate-spin" /></div> : (
        <DataTable
          rows={rows}
          columns={[
            { key: "calc_no", header: "No.", render: (r) => <Link to={`/app/hpp/${r.id}`} className="font-semibold text-emerald-700 hover:underline">{r.calc_no}</Link> },
            { key: "kind", header: "Jenis", render: (r) => <span className={`gc-badge ${r.kind === "actual" ? "bg-cyan-50 text-cyan-700 border border-cyan-200" : "bg-slate-100 text-slate-700"}`}>{r.kind === "actual" ? "Aktual" : "Standar"}</span> },
            { key: "product", header: "Produk", render: (r) => products[r.product_id]?.name || "-" },
            { key: "good_qty", header: "Qty", align: "right", render: (r) => NUM(r.good_qty, 0) },
            { key: "total_cost", header: "Total Biaya", align: "right", render: (r) => <span className="font-semibold">{IDR(r.total_cost)}</span> },
            { key: "hpp_per_unit", header: "HPP/Unit", align: "right", render: (r) => <span className="font-semibold text-emerald-700">{IDR(r.hpp_per_unit)}</span> },
            { key: "created_at", header: "Dibuat", render: (r) => DATE(r.created_at) },
            { key: "status", header: "Status", render: (r) => <span className={`gc-badge ${r.status === "approved" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-slate-100 text-slate-700"}`}>{r.status === "approved" ? "Disetujui" : "Draft"}</span> },
            { key: "actions", header: "Aksi", render: (r) => (
              <div className="flex items-center gap-1 justify-end">
                {r.status !== "approved" && canApprove(user) && (
                  <button onClick={(e) => { e.stopPropagation(); approve(r); }} className="p-1.5 rounded-lg hover:bg-emerald-50 text-emerald-700" title="Setujui" data-testid={`btn-approve-hpp-${r.id}`}><CheckCircle2 className="w-4 h-4" /></button>
                )}
              </div>
            )},
          ]}
          empty={<EmptyState title="Belum ada perhitungan HPP" desc="Mulai dengan menghitung HPP standar dari mix design." action={<button onClick={() => setOpenStd(true)} className="gc-btn-primary"><Calculator className="w-4 h-4" /> Hitung HPP Standar</button>} />}
        />
      )}

      <Dialog open={openStd} onOpenChange={setOpenStd}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-cabinet">Hitung HPP Standar</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="gc-label block mb-1.5">Mix Design *</label>
              <select className="gc-input" value={selectedMD} onChange={(e) => setSelectedMD(e.target.value)} data-testid="input-md-std">
                <option value="">— Pilih —</option>
                {mixDesigns.map((m) => <option key={m.id} value={m.id}>{m.code} — {m.name}</option>)}
              </select>
            </div>
            <div>
              <label className="gc-label block mb-1.5">Target Produksi</label>
              <input type="number" className="gc-input" value={targetQty} onChange={(e) => setTargetQty(e.target.value)} data-testid="input-target-std" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setOpenStd(false)} className="gc-btn-ghost">Batal</button>
              <button onClick={calcStd} disabled={!selectedMD} className="gc-btn-primary" data-testid="btn-run-std">Hitung</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
